import React, { ReactNode, useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ErrorBoundary } from '../../../../components/common/ErrorBoundary'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { CalendarList } from '../../../../components/society/CalendarList'
import { ViewBase } from '../../../../components/common/ViewBase'
import { IconChoices } from '../../../../components/common/Icon'
import { useModal } from '../../../../hooks/useModal'
import { useAuthenticatedUserId } from '../../../../hooks/useAuthenticatedUserId'
import {
  isUserAdminInSociety,
  isUserBoardMemberInSociety,
} from '../../../../helpers/society'
import { ButtonVariant } from '../../../../components/common/Button'
import { CreateUpdateEventModal } from '../../../../components/society/Event/CreateUpdateEventModal'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import { eventQueries } from '../../../../api/events'
import { IEventModel } from '../../../../interfaces/models/events.interfaces'

export enum ActiveTab {
  UPCOMING = 0,
  PREVIOUS = 1,
}

export const CalendarView = (): ReactNode => {
  const { translate } = useAppTranslation()
  const userId = useAuthenticatedUserId()
  const { society } = useCurrentSociety()
  useDocumentTitle(
    reverseDocumentTitle('society:calendar', {
      '{{ societyName }}': society?.name,
    })
  )
  const [activeTab, setActiveTab] = useState<ActiveTab>(ActiveTab.UPCOMING)
  const tabs = [
    {
      title: translate('societyCalendarListView.activeTabBar.upcomingTitle'),
    },
    {
      title: translate('societyCalendarListView.activeTabBar.previousTitle'),
    },
  ]

  const {
    show: showCreateModal,
    open: openCreateModal,
    close: closeCreateModal,
  } = useModal()

  const { data: eventsData, isLoading: isLoadingEvents } = useQuery(
    eventQueries.getEventsPerSociety(society?._id)
  )
  const isAdmin = isUserAdminInSociety(society, userId as string)
  const isBoard = isUserBoardMemberInSociety(society, userId as string)

  const getEvents = useCallback((): {
    [key: string]: IEventModel[]
  } => {
    const now = new Date()
    const _events = eventsData || []
    return _events?.reduce(
      (
        prev: {
          [key: string]: IEventModel[]
        },
        event
      ) => {
        const group = new Date(event.endDate) < now ? 'previous' : 'upcoming'
        prev[group].push(event)
        return prev
      },
      { previous: [], upcoming: [] }
    )
  }, [eventsData])

  const segmentedEvents = getEvents()

  const events =
    activeTab === ActiveTab.UPCOMING
      ? segmentedEvents.upcoming.reverse()
      : segmentedEvents.previous

  const loading = isLoadingEvents

  return (
    <ErrorBoundary>
      <ViewBase
        title={translate('societyCalendarListView.title')}
        tabBar={{ tabs, setActiveTab, activeTab }}
        buttons={[
          ...(isAdmin || isBoard
            ? [
                {
                  label: translate('societyCalendarListView.buttons.create'),
                  variant: ButtonVariant.PRIMARY,
                  icon: IconChoices.PLUS_SIGN,
                  onClick: openCreateModal,
                },
              ]
            : []),
        ]}
      >
        <CalendarList
          events={events}
          userIsAdmin={isAdmin || isBoard}
          loading={loading}
        />
      </ViewBase>
      {(isAdmin || isBoard) && (
        <CreateUpdateEventModal
          show={showCreateModal}
          close={closeCreateModal}
        />
      )}
    </ErrorBoundary>
  )
}
